import { useEffect, useRef, useState } from 'react'
import type { PrinterDriver } from '@alpha-edge/shared'
import { renderPreview } from '../../utils/label-preview'
import { DPI_OPTIONS, type LabelaryDpi } from '../../utils/label-preview/labelary-client'
import { useDebounce } from '../../hooks/use-debounce'
import { colors, input } from './styles'

// Self-contained, reusable label-format preview. Renders an anonymized
// preview (synthetic data only — see utils/label-preview) of a label
// template body. Never blocks the surrounding UI: loading/error states are
// local to this component. (7.6.22 Slice 5, ports legacy
// `Alpha-App/src/components/LabelPreview/LabelPreview.jsx`.)

interface LabelPreviewProps {
  readonly driver: PrinterDriver
  readonly body: string
  readonly paperSize?: string
}

type Status = 'idle' | 'loading' | 'ready' | 'error'

const PREVIEW_DEBOUNCE_MS = 500

function isBlobUrl(url: string): boolean {
  return url.startsWith('blob:')
}

export function LabelPreview({ driver, body, paperSize }: LabelPreviewProps) {
  const [status, setStatus] = useState<Status>('idle')
  const [imgUrl, setImgUrl] = useState('')
  const [errorMsg, setErrorMsg] = useState('')
  const [dpi, setDpi] = useState<LabelaryDpi>(203)

  const debouncedBody = useDebounce(body, PREVIEW_DEBOUNCE_MS)
  const currentUrlRef = useRef<string | null>(null)

  // No body to render — show the idle state without needing an effect to
  // synchronize it (avoids the react-hooks/set-state-in-effect anti-pattern
  // for what is purely derived state).
  const displayStatus: Status = debouncedBody ? status : 'idle'

  function replaceUrl(url: string | null) {
    if (currentUrlRef.current && isBlobUrl(currentUrlRef.current)) {
      URL.revokeObjectURL(currentUrlRef.current)
    }
    currentUrlRef.current = url
  }

  useEffect(() => {
    if (!debouncedBody) return
    let active = true
    // eslint-disable-next-line react-hooks/set-state-in-effect -- async render + mid-render loading state is exactly what this effect is for
    setStatus('loading')

    renderPreview({ driver, body: debouncedBody, paperSize, dpi })
      .then((url) => {
        if (!active) {
          if (isBlobUrl(url)) URL.revokeObjectURL(url)
          return
        }
        replaceUrl(url)
        setImgUrl(url)
        setStatus('ready')
      })
      .catch((e: unknown) => {
        if (!active) return
        const code = (e as { code?: string } | undefined)?.code
        let msg: string
        if (code === 'DYMO_NOT_AVAILABLE') msg = 'Preview requires DYMO Connect'
        else if (code === 'LABELARY_UNREACHABLE') msg = 'Label preview service unreachable'
        else if (e instanceof Error && e.message) msg = e.message
        else msg = 'Preview unavailable'
        setErrorMsg(msg)
        setStatus('error')
      })

    return () => {
      active = false
    }
  }, [driver, debouncedBody, paperSize, dpi])

  // Revoke the last object URL on unmount.
  useEffect(() => {
    return () => {
      replaceUrl(null)
    }
  }, [])

  return (
    <div
      style={{ border: `1px solid ${colors.border}`, borderRadius: '6px', padding: '0.5rem' }}
      data-testid="label-preview"
    >
      {driver === 'zebra_browser_print' && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
          <label htmlFor="label-preview-dpi" style={{ fontSize: '0.8125rem', color: colors.textMuted }}>
            DPI:
          </label>
          <select
            id="label-preview-dpi"
            aria-label="DPI"
            style={{ ...input, width: 'auto', height: 'auto', padding: '0.25rem 0.5rem' }}
            value={dpi}
            onChange={(e) => setDpi(Number(e.target.value) as LabelaryDpi)}
          >
            {DPI_OPTIONS.map((d) => (
              <option key={d} value={d}>
                {d}
              </option>
            ))}
          </select>
        </div>
      )}

      {displayStatus === 'loading' && (
        <div style={{ color: colors.textMuted, fontSize: '0.8125rem' }}>Rendering preview...</div>
      )}
      {displayStatus === 'error' && <div style={{ color: colors.danger, fontSize: '0.8125rem' }}>{errorMsg}</div>}
      {displayStatus === 'ready' && (
        <img src={imgUrl} alt="Label preview" style={{ maxWidth: '100%', maxHeight: '220px' }} />
      )}
      {displayStatus === 'idle' && (
        <div style={{ color: colors.textMuted, fontSize: '0.8125rem' }}>Nothing to preview.</div>
      )}
    </div>
  )
}
